import { useId, useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi } from "../api/hooks";
import type { LocationInput, Place } from "../api/types";
import { can } from "../auth/roles";
import { StatusBadge } from "../components/Badges";
import { ACCURACY_CLASS, PLACE_KIND, labelOf, statusOf } from "../components/labels";
import { Notice, ProblemAlert } from "../components/ProblemAlert";
import { Tabs } from "../components/Tabs";
import { useFireContext } from "../context";
import { formatDateTime } from "../format/datetime";
import { FireMap } from "../map/FireMap";
import type { LngLat, MapData } from "../map/layers";
import { metres, useWardNames } from "./shared";
import { Button } from "@lcc/ui";

/** A gazetteer place as the contract's `LocationInput`: landmarks are `ward_landmark`, streets, plots and wards `geocoded`. */
export function placeLocation(p: Place): LocationInput {
  return {
    source: p.kind === "landmark" ? "ward_landmark" : "geocoded",
    lon: p.lon,
    lat: p.lat,
    accuracy_m: p.accuracy_m,
    place_id: p.place_id,
    description: p.context ? `${p.name}, ${p.context}` : p.name,
  };
}

/** A typed or clicked pin as a `LocationInput` (`map_pin`), or an error in words. */
export function pinLocation(lon: string, lat: string, accuracy: string): LocationInput | string {
  const x = Number(lon);
  const y = Number(lat);
  const a = Number(accuracy);
  if (!lon.trim() || !lat.trim() || !Number.isFinite(x) || !Number.isFinite(y) || x < -180 || x > 180 || y < -90 || y > 90)
    return "Give the pin's longitude (-180 to 180) and latitude (-90 to 90), or click the map.";
  if (!Number.isFinite(a) || a <= 0 || a > 100000) return "Give how sure the pin is, in metres (more than 0, at most 100000).";
  return { source: "map_pin", lon: Math.round(x * 1e6) / 1e6, lat: Math.round(y * 1e6) / 1e6, accuracy_m: a };
}

/** Search FER's cached gazetteer (works with gis down; `cache_as_of` says how fresh it is). */
export function PlaceSearch({ onChoose, disabled }: { onChoose: (p: Place) => void; disabled?: boolean }) {
  const [q, setQ] = useState("");
  const [asked, setAsked] = useState("");
  const wardName = useWardNames();
  const id = useId();
  const res = useApi(["places", asked], (api) => endpoints.searchPlaces(api, asked), { enabled: asked.trim().length >= 2, staleTime: 5 * 60_000 });
  const items = res.data?.items ?? [];
  return (
    <div className="fer-form">
      <div className="fer-field">
        <label htmlFor={`${id}-q`}>
          <span>Place name, street or landmark</span>
        </label>
        <div className="fer-inline">
          <input
            id={`${id}-q`}
            value={q}
            maxLength={200}
            disabled={disabled}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setAsked(q.trim());
              }
            }}
          />
          <Button variant="secondary" size="sm" disabled={disabled || q.trim().length < 2} onClick={() => setAsked(q.trim())}>
            Search places
          </Button>
        </div>
        <span className="fer-hint">At least 2 letters. Searches FER's own copy of the place names, so it works while the map service is down.</span>
      </div>
      <ProblemAlert error={res.error} />
      {res.data && (
        <>
          {res.data.cache_as_of ? (
            <p className="fer-hint">Place names as of {formatDateTime(res.data.cache_as_of)}.</p>
          ) : (
            <Notice tone="warning">No place names have been loaded yet. Drop a pin instead.</Notice>
          )}
          {items.length === 0 ? (
            <p className="fer-muted">No place matches “{asked}”. Try another spelling or drop a pin.</p>
          ) : (
            <table className="lcc-table" aria-label={`Places matching ${asked}`}>
              <thead>
                <tr>
                  <th scope="col">Place</th>
                  <th scope="col">Kind</th>
                  <th scope="col">Ward</th>
                  <th scope="col">Plus Code</th>
                  <th scope="col">Within</th>
                  <th scope="col">
                    <span className="lcc-sr-only">Choose</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.place_id}>
                    <th scope="row">
                      {p.name}
                      {p.context ? <span className="fer-muted"> – {p.context}</span> : null}
                    </th>
                    <td>{labelOf(PLACE_KIND, p.kind)}</td>
                    <td>{wardName(p.ward_id)}</td>
                    <td>{p.plus_code}</td>
                    <td>{metres(p.accuracy_m)}</td>
                    <td>
                      <Button variant="secondary" size="sm" disabled={disabled} onClick={() => onChoose(p)} aria-label={`Use ${p.name}`}>
                        Use this place
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

/** The ward, service area, accuracy class and Plus Code of a point (FER's cached wards). */
export function WardOf({ lon, lat, accuracyM }: { lon: number; lat: number; accuracyM?: number }) {
  const { principal } = useFireContext();
  const q = useApi(["ward-lookup", lon, lat, accuracyM ?? null], (api) => endpoints.lookupWard(api, lon, lat, accuracyM), {
    enabled: can(principal, "lookupWard"),
    staleTime: 60_000,
  });
  if (!q.data) return <ProblemAlert error={q.error} />;
  const d = q.data;
  return (
    <p className="fer-hint" role="status">
      {d.in_service_area ? `Ward: ${d.ward_name ?? d.ward_id}` : "Outside every Lusaka ward (mutual aid)"}; Plus Code {d.plus_code}
      {d.accuracy_class ? (
        <>
          {" "}
          <StatusBadge status={statusOf(ACCURACY_CLASS, d.accuracy_class)} />
        </>
      ) : null}
    </p>
  );
}

/** Drop a pin (map click or typed coordinates) with the call-taker's estimate of how sure it is. */
export function PinDrop({ onPin, disabled, context }: { onPin: (l: LocationInput) => void; disabled?: boolean; context?: MapData }) {
  const [lon, setLon] = useState("");
  const [lat, setLat] = useState("");
  const [acc, setAcc] = useState("100");
  const [err, setErr] = useState<string | null>(null);
  const id = useId();
  const clicked: LngLat | null = lon && lat && Number.isFinite(Number(lon)) && Number.isFinite(Number(lat)) ? [Number(lon), Number(lat)] : null;
  const use = () => {
    const l = pinLocation(lon, lat, acc);
    if (typeof l === "string") return setErr(l);
    setErr(null);
    onPin(l);
  };
  return (
    <div className="fer-form">
      <FireMap
        ariaLabel="Map to drop a pin: click where the caller is"
        data={{ ...(context ?? {}), pin: clicked }}
        pinMode={!disabled}
        height={320}
        onPin={([x, y]) => {
          setLon(x.toFixed(6));
          setLat(y.toFixed(6));
        }}
      />
      <div className="fer-form-grid">
        <div className="fer-field">
          <label htmlFor={`${id}-lon`}>
            <span>Pin longitude</span>
          </label>
          <input id={`${id}-lon`} inputMode="decimal" value={lon} disabled={disabled} onChange={(e) => setLon(e.target.value)} />
        </div>
        <div className="fer-field">
          <label htmlFor={`${id}-lat`}>
            <span>Pin latitude</span>
          </label>
          <input id={`${id}-lat`} inputMode="decimal" value={lat} disabled={disabled} onChange={(e) => setLat(e.target.value)} />
        </div>
        <div className="fer-field">
          <label htmlFor={`${id}-acc`}>
            <span>Pin is within (metres)</span>
          </label>
          <input id={`${id}-acc`} inputMode="numeric" value={acc} disabled={disabled} onChange={(e) => setAcc(e.target.value)} />
        </div>
      </div>
      {clicked && <WardOf lon={clicked[0]} lat={clicked[1]} accuracyM={Number(acc) > 0 ? Number(acc) : undefined} />}
      {err && (
        <p className="fer-form-error" role="alert">
          {err}
        </p>
      )}
      <div className="fer-actions fer-actions--start">
        <Button variant="secondary" size="sm" disabled={disabled} onClick={use}>
          Use this pin
        </Button>
      </div>
    </div>
  );
}

/** Choose a location by place search or pin; shows the choice in words. */
export function LocationPicker({ value, onChange, disabled, context }: { value: LocationInput | null; onChange: (l: LocationInput | null) => void; disabled?: boolean; context?: MapData }) {
  const [tab, setTab] = useState<"search" | "pin">("search");
  return (
    <div className="fer-subsection">
      {value ? (
        <p role="status">
          <strong>Chosen location:</strong> {value.description ?? `pin at ${value.lon.toFixed(5)}, ${value.lat.toFixed(5)}`} (within {metres(value.accuracy_m)}){" "}
          {!disabled && (
            <button type="button" className="fer-link" onClick={() => onChange(null)}>
              Clear the location
            </button>
          )}
        </p>
      ) : (
        <p className="fer-muted">No location chosen.</p>
      )}
      <Tabs
        label="How to give the location"
        tabs={[
          { key: "search", label: "Search a place" },
          { key: "pin", label: "Drop a pin" },
        ]}
        current={tab}
        onChange={setTab}
      >
        {tab === "search" ? <PlaceSearch disabled={disabled} onChoose={(p) => onChange(placeLocation(p))} /> : <PinDrop disabled={disabled} onPin={onChange} context={context} />}
      </Tabs>
    </div>
  );
}
